import { useCallback, useState } from "react"
import { InspectorPanelKeyValueDropdown } from "../InspectorPanelKeyValueDropdown"
import {
    hexColorSwatches,
    ColorSwatchCategory,
    LineWidth,
    lineWidthNameByLineWidth,
    FirePerimeterMethod,
} from "@nova/constants"
import { InspectorPanelSettingsFields } from "../InspectorPanelSettingsFields"
import * as Icons from "@nova/component-icons"
import { decodeNumberToColor, encodeColorToNumber } from "@nova/serializer/src/client/util"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { ColorPalette } from "../icons/TODO-put-in-package/ColorPalette"
import { selectLineOutlineById, selectLineWidthById } from "features/entity-caching/lines/selectors"
import { useOptimisticLocalState } from "../useOptimisticLocalState"
import { LineThicknessPicker } from "../lines/LineThicknessPicker"
import { thicknesses } from "../lines/constants"
import { updatePerimeter } from "features/pubsub/entityActions/cubes/perimeter"
import { ButtonTertiary } from "@nova/component-btn"
import { InspectorPanelFirePerimeterLayerBuffers } from "./buffers/InspectorPanelFirePerimeterLayerBuffers"
import { calculateArea } from "@nova/calculate-geospatial"
import {
    changeUpdateMethod,
    moveFirePerimeterToEditable,
} from "plugins/DrawPerimeter/FirePerimeterSlice"
import { selectFirePerimeterById } from "features/entity-caching/fire-perimeters/selectors"

const firePerimeterOutlineColorSwatches = hexColorSwatches[
    ColorSwatchCategory.FirePerimeterOutline
].map((hex) => encodeColorToNumber(hex))

export const InspectorPanelFirePerimeterLayer = ({ id }: { id: string }) => {
    const dispatch = useAppDispatch()
    const perimeter = useAppSelector(selectFirePerimeterById(id))

    const [openCategories, setOpenCategories] = useState(new Set<string>())

    // This does NOT refer to categories, but rather individual
    // dropdowns that exist inside of any category.
    const [openDropdownId, setOpenDropdownId] = useState<string>()

    const setCategoryIsOpen = useCallback((category: string, isOpen: boolean) => {
        setOpenCategories((prev) => {
            const openCategories = new Set(prev)
            if (isOpen) openCategories.add(category)
            else openCategories.delete(category)
            return openCategories
        })
    }, [])

    const [outline, setOutline] = useOptimisticLocalState<number>(
        selectLineOutlineById(id),
        (outline) => {
            if (!perimeter) return
            dispatch(updatePerimeter({ id, updates: { outline } }))
        },
        perimeter.outline || firePerimeterOutlineColorSwatches[0]
    )

    const [width, setWidth] = useOptimisticLocalState<LineWidth>(
        selectLineWidthById(perimeter.id),
        (width) => {
            if (!perimeter) return
            dispatch(updatePerimeter({ id, updates: { width } }))
        },
        perimeter.width || LineWidth.normal
    )

    if (!perimeter) return null

    return (
        <div className="flex flex-col gap-2 mb-2">
            {/* Settings */}
            <InspectorPanelSettingsFields>
                {/* Outline (polygons only, not lines, unless user is choosing colors for a mocked polygon) */}
                <InspectorPanelKeyValueDropdown
                    id="outline"
                    isOpen={openDropdownId === "outline"}
                    setOpenDropdownId={setOpenDropdownId}
                    keyTitle="Outline"
                    Icon={Icons.BucketColor}
                    iconClasses="text-gray-0"
                    iconAccentColor={decodeNumberToColor(outline)}
                >
                    <ColorPalette
                        swatches={firePerimeterOutlineColorSwatches}
                        selectedColor={outline}
                        setSelectedColor={setOutline}
                        setOpenDropdownId={setOpenDropdownId}
                    />
                </InspectorPanelKeyValueDropdown>

                {/* Thickness */}
                <InspectorPanelKeyValueDropdown
                    id="thickness"
                    isOpen={openDropdownId === "thickness"}
                    setOpenDropdownId={setOpenDropdownId}
                    keyTitle="Thickness"
                    Icon={thicknesses[width]}
                    dropdownTitle={lineWidthNameByLineWidth[width]}
                    iconClasses="text-gray-0"
                >
                    <LineThicknessPicker
                        selectedThickness={width}
                        setSelectedThickness={setWidth}
                        setOpenDropdownId={setOpenDropdownId}
                    />
                </InspectorPanelKeyValueDropdown>
            </InspectorPanelSettingsFields>

            <ButtonTertiary
                title="Update Shape"
                LeftIcon={Icons.PencilSquare}
                className="mx-4 -mt-2"
                onClick={() => {
                    dispatch(changeUpdateMethod({ method: FirePerimeterMethod.None }))
                    if (!perimeter.polygons.length) {
                        dispatch(moveFirePerimeterToEditable({ perimeter }))
                        return
                    }
                    const largestPolygon = [...perimeter.polygons]
                        .map((polygon, i) => ({ ...polygon, index: i }))
                        .sort(
                            (a, b) => calculateArea(b.coordinates) - calculateArea(a.coordinates)
                        )[0].index

                    if (largestPolygon > -1)
                        dispatch(
                            moveFirePerimeterToEditable({
                                perimeter,
                                currentIndex: largestPolygon,
                            })
                        )
                }}
            />

            {/* Buffers */}
            <InspectorPanelFirePerimeterLayerBuffers
                id={id}
                openDropdownId={openDropdownId}
                setOpenDropdownId={setOpenDropdownId}
                openCategories={openCategories}
                setCategoryIsOpen={setCategoryIsOpen}
            />
        </div>
    )
}
